import React from "react";
import Grid from "@mui/material/Grid";

// Backstage Core & Catalog
import {
	EntityAboutCard,
	EntityLinksCard,
	EntitySwitch,
} from "@backstage/plugin-catalog";

// CI/CD & Build Tools
import {
	EntityLatestJenkinsRunCard,
	isJenkinsAvailable,
} from "@backstage/plugin-jenkins";
import {
	EntityArgoCDOverviewCard,
	isArgocdAvailable,
} from "@roadiehq/backstage-plugin-argo-cd";

// Code Quality & Security
import { EntitySonarQubeCard } from "@backstage/plugin-sonarqube";
import { isSonarQubeAvailable } from "@backstage/plugin-sonarqube-react";
import {
	EntityDependabotAlertsCard,
	isSecurityInsightsAvailable,
} from "@roadiehq/backstage-plugin-security-insights";
import {
	EntityDependencytrackFindingCard,
	EntityDependencytrackSummaryCard,
	isDependencytrackAvailable,
} from "@trimm/plugin-dependencytrack";
import {
	EntityGrafanaDashboardsCard,
  } from '@k-phoen/backstage-plugin-grafana';
// Monitoring & Observability
import {
	EntityPrometheusAlertCard,
	EntityPrometheusGraphCard,
	isPrometheusAvailable,
} from "@roadiehq/backstage-plugin-prometheus";
import {
	EntityPagerDutyCard,
	isPluginApplicableToEntity as isPagerDutyAvailable,
} from "@backstage/plugin-pagerduty";
import {
	EntityLastLighthouseAuditCard,
	isLighthouseAvailable,
} from "@backstage/plugin-lighthouse";

// Cloud & Infrastructure
import {
	EntityAWSLambdaOverviewCard,
	isAWSLambdaAvailable,
} from "@roadiehq/backstage-plugin-aws-lambda";

// Documentation
import { ReadmeCard } from "@axis-backstage/plugin-readme";

// Local Components
import { entityWarningContent } from "./EntityWarning";

// Helper component to wrap cards in Grid items with conditional rendering
interface ConditionalCardProps {
	condition: (entity: any) => boolean;
	children: React.ReactNode;
}

const ConditionalCard: React.FC<ConditionalCardProps> = ({ condition, children }) => (
	<EntitySwitch>
		<EntitySwitch.Case if={condition}>
			<Grid item xs={12}>
				{children}
			</Grid>
		</EntitySwitch.Case>
	</EntitySwitch>
);

export const overviewContent = (
	<Grid container spacing={3}>
		{/* Entity Warning */}
		<Grid item xs={12}>
			{entityWarningContent}
		</Grid>

		{/* About Card */}
		<Grid item xs={12}>
			<EntityAboutCard />
		</Grid>

		{/* Documentation */}
		<Grid item xs={12}>
			<ReadmeCard />
		</Grid>

		{/* Monitoring & Observability */}
		<EntitySwitch>
			<EntitySwitch.Case if={isPrometheusAvailable}>
				<Grid item xs={12}>
					<EntityPrometheusAlertCard />
				</Grid>
				<Grid item xs={12}>
					<EntityPrometheusGraphCard />
				</Grid>
			</EntitySwitch.Case>
		</EntitySwitch>

		{/* Code Quality */}
		<ConditionalCard condition={isSonarQubeAvailable}>
			<EntitySonarQubeCard variant="gridItem" />
		</ConditionalCard>

		{/* Security & Dependency Tracking */}
		<EntitySwitch>
			<EntitySwitch.Case if={isDependencytrackAvailable}>
				<Grid item xs={12}>
					<EntityDependencytrackSummaryCard />
				</Grid>
				<Grid item xs={12}>
					<EntityDependencytrackFindingCard />
				</Grid>
			</EntitySwitch.Case>
		</EntitySwitch>
		<Grid container spacing={3} alignItems="stretch">
			<Grid item md={6}>
			<EntityAboutCard variant="gridItem" />
			</Grid>

			<Grid item md={6}>
			{/* Grafana alert card start */}
			<EntityGrafanaDashboardsCard />
			{/* Grafana alert card end */}
			</Grid>

			{/* <Grid item md={4} xs={12}>
			<EntityLinksCard />
			</Grid>
			<Grid item md={8} xs={12}>
			<EntityHasSubcomponentsCard variant="gridItem" />
			</Grid> */}
		</Grid>
		<ConditionalCard condition={isSecurityInsightsAvailable}>
			<EntityDependabotAlertsCard />
		</ConditionalCard>

		{/* CI/CD & Deployments */}
		<ConditionalCard condition={isArgocdAvailable}>
			<EntityArgoCDOverviewCard />
		</ConditionalCard>

		<ConditionalCard condition={(e) => Boolean(isAWSLambdaAvailable(e))}>
			<EntityAWSLambdaOverviewCard />
		</ConditionalCard>

		<ConditionalCard condition={isJenkinsAvailable}>
			<EntityLatestJenkinsRunCard branch="main,master" />
		</ConditionalCard>

		{/* Links */}
		<Grid item xs={12}>
			<EntityLinksCard />
		</Grid>

		{/* Performance & Monitoring */}
		<ConditionalCard condition={isLighthouseAvailable}>
			<EntityLastLighthouseAuditCard />
		</ConditionalCard>

		<ConditionalCard condition={isPagerDutyAvailable}>
			<EntityPagerDutyCard />
		</ConditionalCard>
	</Grid>
);
